// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/formatToolStepBody.test.ts
import { parseTruncatedJson, stringField } from "./truncatedJson";

type IntentDraft = {
  area?: unknown;
  suspected_issue?: unknown;
  why_it_matters?: unknown;
  severity_guess?: unknown;
};

function formatIntentsFromDrafts(
  parsed: Record<string, unknown>,
): string | null {
  const intents = parsed.intents;
  if (!Array.isArray(intents) || intents.length === 0) return null;

  const lines: string[] = [
    intents.length === 1
      ? "1 suspected issue"
      : `${intents.length} suspected issues`,
  ];

  let shown = 0;
  for (const item of intents) {
    if (item == null || typeof item !== "object") continue;
    shown += 1;
    const intent = item as IntentDraft;
    const area = stringField(intent.area);
    const severity = stringField(intent.severity_guess);
    const issue = stringField(intent.suspected_issue);
    const why = stringField(intent.why_it_matters);

    const header = [area || "Issue", severity ? `(${severity})` : ""]
      .filter(Boolean)
      .join(" ");
    lines.push("");
    lines.push(`${shown}. ${header}`);
    if (issue) lines.push(`   ${issue}`);
    if (why) lines.push(`   Why it matters: ${why}`);
  }

  return shown > 0 ? lines.join("\n").trim() : null;
}

function formatSubagentLikeReport(
  parsed: Record<string, unknown>,
): string | null {
  const summary = stringField(parsed.summary);
  if (summary && !summary.startsWith("{") && !summary.startsWith("[")) {
    return summary;
  }
  const error = stringField(parsed.error);
  if (error) return error;
  // A subagent's `output` is its typed result serialized to a JSON string.
  const output = formatToolStepBody(stringField(parsed.output));
  if (output) return output;
  const message = stringField(parsed.message);
  if (message) return message;
  const instructions = stringField(parsed.instructions);
  if (instructions) return instructions;
  const text = stringField(parsed.text);
  if (text) return text;
  return null;
}

function formatDraftCount(parsed: Record<string, unknown>): string | null {
  if (typeof parsed.drafts === "number") {
    const count = parsed.drafts;
    return count === 1 ? "1 draft" : `${count} drafts`;
  }
  return null;
}

// why: "[1]" and "[-]" are task prose; a digit or "-" after "[" is not JSON.
const JSON_PAYLOAD_START =
  /^(\{\s*["}]|\[\s*["[{]|\[\s*(true|false|null)\b|\[\s*\])/;

// Identifiers, page geometry, and bookkeeping a reader cannot act on.
const HIDDEN_KEYS = new Set([
  "id",
  "_id",
  "tool_calls",
  "input_tokens",
  "output_tokens",
  "bbox",
  "region",
  "pin_x",
  "pin_y",
  "knowledge_citations",
]);

const MAX_READABLE_DEPTH = 4;

function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : key;
}

function isHiddenKey(key: string): boolean {
  return HIDDEN_KEYS.has(key) || /(^|_)id$/.test(key) || /[a-z]Id$/.test(key);
}

function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.every(isEmptyValue);
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).every(
      ([key, child]) => isHiddenKey(key) || isEmptyValue(child),
    );
  }
  return false;
}

function scalarText(value: unknown): string | null {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function indent(lines: string[], prefix: string): string[] {
  return lines.map((line) => `${prefix}${line}`);
}

function readableLines(value: unknown, depth: number): string[] {
  const scalar = scalarText(value);
  if (scalar != null) return scalar ? [scalar] : [];
  if (depth > MAX_READABLE_DEPTH) return [];

  if (Array.isArray(value)) {
    const items = value.filter((item) => !isEmptyValue(item));
    return items.flatMap((item, index) => {
      const itemScalar = scalarText(item);
      if (itemScalar != null) return [`- ${itemScalar}`];
      const [first, ...rest] = readableLines(item, depth + 1);
      if (first == null) return [];
      return [`${index + 1}. ${first}`, ...indent(rest, "   ")];
    });
  }

  if (value != null && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>).flatMap(
      ([key, child]) => {
        if (isHiddenKey(key) || isEmptyValue(child)) return [];
        const label = humanizeKey(key);
        if (
          key === "confidence" &&
          typeof child === "number" &&
          child >= 0 &&
          child <= 1
        ) {
          return [`${label}: ${Math.round(child * 100)}%`];
        }
        const childScalar = scalarText(child);
        if (childScalar != null) return [`${label}: ${childScalar}`];
        const nested = readableLines(child, depth + 1);
        return nested.length > 0 ? [`${label}:`, ...indent(nested, "  ")] : [];
      },
    );
  }
  return [];
}

/**
 * Structured JSON as labelled lines ("Suspected issue: ...") with ids and
 * token bookkeeping dropped, for payloads no specific formatter recognizes.
 */
export function readableJson(value: unknown): string {
  return readableLines(value, 0).join("\n").trim();
}

function tryParseJson(text: string): unknown | null {
  if (!JSON_PAYLOAD_START.test(text)) return null;
  // why: christo caps every warhol summary at 500 chars, so a structured
  // return usually arrives cut mid-object; the salvage keeps what fit.
  return parseTruncatedJson(text, true) ?? null;
}

/**
 * Turn a warhol tool_result summary into readable prose. Christo stores
 * structured returns as JSON strings; raw JSON in the accordion body is
 * unreadable, so known shapes get a specific rendering and anything else
 * becomes labelled lines.
 */
export function formatToolStepBody(text: string, tool?: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";

  const parsed = tryParseJson(trimmed);
  if (parsed == null) {
    return JSON_PAYLOAD_START.test(trimmed) ? "" : trimmed;
  }

  if (Array.isArray(parsed)) {
    const parts = parsed
      .map((entry) => {
        if (entry == null || typeof entry !== "object") {
          return scalarText(entry) ?? "";
        }
        const record = entry as Record<string, unknown>;
        return formatSubagentLikeReport(record) ?? readableJson(record);
      })
      .filter(Boolean);
    return parts.join("\n\n");
  }

  if (typeof parsed === "object" && parsed !== null) {
    const obj = parsed as Record<string, unknown>;

    if (tool === "intents_from_drafts" || obj.intents != null) {
      const formatted = formatIntentsFromDrafts(obj);
      if (formatted) return formatted;
    }

    const reportLike = formatSubagentLikeReport(obj);
    if (reportLike) return reportLike;

    const draftCount = formatDraftCount(obj);
    if (draftCount) return draftCount;

    for (const key of ["result", "content", "description"]) {
      const val = stringField(obj[key]);
      if (val) return val;
    }

    return readableJson(obj);
  }

  return scalarText(parsed) ?? "";
}
